import { useState, useMemo } from 'react';
import { usePartnersLists } from '@/features/partners/hooks/usePartnersPage';
import { SectionWrapper } from './SectionWrapper';
import { SectionTitle } from './SectionTitle';
import { PartnerCard } from './PartnerCard';
import { CategoryTabs } from './CategoryTabs';
import { Skeleton } from '@/components/ui/Skeleton';

interface PartnersGridProps {
  showFilters?: boolean;
  title?: string;
  subtitle?: string;
  categoryLabel?: string;
}

export function PartnersGrid({
  showFilters = true,
  title = 'Our Partners',
  subtitle = 'Trusted companies we work with',
  categoryLabel,
}: PartnersGridProps) {
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const { categories, isLoading, isError } = usePartnersLists();

  // Filter categories when one is selected
  const displayedCategories = useMemo(() => {
    if (!categories) return [];
    if (selectedCategory === null) return categories;

    return categories.filter(c => c.id === selectedCategory);
  }, [categories, selectedCategory]);

  // Check if we have any partners across all displayed categories
  const hasPartners = useMemo(() => {
    return displayedCategories.some(c => c.partners && c.partners.length > 0);
  }, [displayedCategories]);

  if (isLoading) {
    return <PartnersGridSkeleton />;
  }

  if (isError) {
    return (
      <SectionWrapper background="gray">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Failed to load partners. Please try again later.</p>
        </div>
      </SectionWrapper>
    );
  }

  // If no categories or no partners at all (and no loading/error), maybe hide
  if (categories.length === 0) {
    return null;
  }

  return (
    <SectionWrapper id="partners" background="primary" aria-labelledby="partners-title">
      <SectionTitle id="partners-title" title={title} subtitle={subtitle} />

      {/* Category Filter */}
      {showFilters && categories.length > 1 && (
        <CategoryTabs
          categories={categories}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
        />
      )}

      {/* Partners by Category */}
      <div className="space-y-12">
        {displayedCategories.map((category) => (
          (category.partners && category.partners.length > 0) ? (
            <div key={category.id}>
              {/* Category Header (only if showing all) */}
              {selectedCategory === null && (
                <h3 className="mb-6 border-b border-border pb-2 text-xl font-semibold text-foreground">
                   {categoryLabel ? `${categoryLabel}: ${category.name}` : category.name}
                   <span className="ml-2 text-sm font-normal text-muted-foreground">
                    ({category.partners.length} partner{category.partners.length !== 1 ? 's' : ''})
                  </span>
                </h3>
              )}

              {/* Partners Grid */}
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                role="list"
                aria-label={`Partners in ${category.name}`}
              >
                {category.partners.map((partner) => (
                  <div key={partner.id} role="listitem">
                    <PartnerCard
                      partner={partner}
                      categoryName={selectedCategory !== null ? undefined : category.name}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null
        ))}
      </div>

      {/* Empty state for filtered view */}
      {!hasPartners && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No partners found in this category.</p>
        </div>
      )}
    </SectionWrapper>
  );
}

function PartnersGridSkeleton() {
  return (
    <SectionWrapper background="gray">
      <div className="mb-12 text-center">
        <Skeleton className="h-10 w-64 mx-auto mb-4" />
        <Skeleton className="h-6 w-96 mx-auto" />
      </div>

      <div className="mb-8 flex justify-center gap-2">
        <Skeleton className="h-10 w-24 rounded-full" />
        <Skeleton className="h-10 w-32 rounded-full" />
        <Skeleton className="h-10 w-28 rounded-full" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <div key={i} className="h-64 rounded-3xl border border-border bg-card p-6 shadow-card">
            <Skeleton className="h-32 w-full mb-4 rounded-lg" />
            <Skeleton className="h-6 w-3/4 mb-2" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ))}
      </div>
    </SectionWrapper>
  );
}

export default PartnersGrid;
